import { useCallback, useEffect, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2, AlertTriangle, XCircle, Shield, ShieldCheck, FileText, User, Calendar, MapPin, Upload, Search, ArrowLeft,
  Loader2, FileCheck, Lock, ExternalLink, QrCode, Fingerprint, Info, ChevronDown, Copy,
} from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL, getApiUrl } from "@/lib/config";
import { Mascot } from "@/components/assistant/mascot";
import { VerifyChat } from "@/components/verify-chat";
import {
  CODE_RX, CaptchaRequiredError, VerifyResult, VerifiedSigner, findVerificationCode, sha256Hex, solveCaptcha, verifyCheck,
} from "@/lib/verify-client";

const ITI_URL = "https://validar.iti.gov.br";

const translations = {
  en: {
    title: "Document Verification",
    subtitle: "Confirm in seconds that a document signed on FillSign is authentic and unchanged",
    dropTitle: "Drop the signed PDF here",
    dropHint: "or click to choose. The file never leaves your device: only its fingerprint (hash) is sent.",
    checking: "Checking…",
    hashing: "Reading the file…",
    orCode: "or type the code printed at the bottom of the PDF",
    codePlaceholder: "FSIG-XXXX-XXXX-XXXX",
    verifyButton: "Verify",
    advanced: "Advanced: verify by hash",
    hashLabel: "SHA-256 of the file (64 hex characters)",
    codeNotFound: "No FillSign code found in this file. If the PDF has a code at the bottom, type it:",
    authentic: "Authentic",
    authenticMsg: "This file is exactly the final PDF signed on FillSign.",
    authenticPending: "This file is the latest version on FillSign, still waiting for {n} signature(s).",
    authenticUnsigned: "This file is the document as uploaded to FillSign; nobody has signed it yet.",
    modified: "Not the original file",
    modifiedMsg: "The document exists on FillSign, but this file is not the signed version: it was changed, scanned, or simply saved again by another program. Ask for the original signed copy.",
    modifiedOriginal: "It matches the file uploaded before the signatures (unsigned version).",
    modifiedPartial: "It matches the version after {n} of {total} signatures, not the final one.",
    unknown: "Not found",
    unknownMsg: "No FillSign code and no known fingerprint: this file was not signed on FillSign, or it was changed.",
    unknownIti: "Signed with an ICP-Brasil certificate? Validate it at",
    registered: "Registered on FillSign",
    registeredMsg: "The code is valid. To confirm your file was not altered, drop the PDF below.",
    documentName: "Document",
    signedAt: "Completed",
    code: "Verification code",
    signers: "Signers",
    signed: "signed",
    pending: "pending",
    declined: "declined",
    viewed: "viewed",
    draw: "Hand-drawn",
    typed: "Typed",
    govbr: "GOV.BR",
    cpf: "CPF",
    phone: "Phone",
    ip: "IP",
    tryAgain: "Verify another document",
    backToHome: "Back to Home",
    privacy: "Nothing is uploaded: your browser computes the SHA-256 and reads the code from the PDF locally.",
    copy: "Copy link",
    copied: "Copied",
    captcha: "Please confirm you are human and try again.",
    failed: "Could not verify right now. Try again in a moment.",
    chatOff: "",
  },
  pt: {
    title: "Verificação de Documentos",
    subtitle: "Confirme em segundos se um documento assinado na FillSign é autêntico e não foi alterado",
    dropTitle: "Solte aqui o PDF assinado",
    dropHint: "ou clique para escolher. O arquivo não sai do seu aparelho: só a impressão digital (hash) é enviada.",
    checking: "Verificando…",
    hashing: "Lendo o arquivo…",
    orCode: "ou digite o código impresso no rodapé do PDF",
    codePlaceholder: "FSIG-XXXX-XXXX-XXXX",
    verifyButton: "Verificar",
    advanced: "Avançado: verificar pelo hash",
    hashLabel: "SHA-256 do arquivo (64 caracteres)",
    codeNotFound: "Não achei um código FillSign neste arquivo. Se o PDF tem o código no rodapé, digite:",
    authentic: "Autêntico",
    authenticMsg: "Este arquivo é exatamente o PDF final assinado na FillSign.",
    authenticPending: "Este arquivo é a versão mais recente na FillSign, ainda aguardando {n} assinatura(s).",
    authenticUnsigned: "Este arquivo é o documento como foi enviado à FillSign; ninguém assinou ainda.",
    modified: "Não é o arquivo original",
    modifiedMsg: "O documento existe na FillSign, mas este arquivo não é a versão assinada: foi alterado, digitalizado ou apenas salvo de novo por outro programa. Peça a cópia assinada original.",
    modifiedOriginal: "Ele bate com o arquivo enviado antes das assinaturas (versão sem assinatura).",
    modifiedPartial: "Ele bate com a versão depois de {n} de {total} assinaturas, não com a final.",
    unknown: "Não encontrado",
    unknownMsg: "Sem código FillSign e sem impressão digital conhecida: este arquivo não foi assinado pela FillSign ou foi alterado.",
    unknownIti: "Assinatura com certificado ICP-Brasil? Valide em",
    registered: "Registrado na FillSign",
    registeredMsg: "O código é válido. Para confirmar que o seu arquivo não foi alterado, solte o PDF abaixo.",
    documentName: "Documento",
    signedAt: "Concluído em",
    code: "Código de verificação",
    signers: "Assinantes",
    signed: "assinou",
    pending: "pendente",
    declined: "recusou",
    viewed: "visualizou",
    draw: "Manuscrita",
    typed: "Digitada",
    govbr: "GOV.BR",
    cpf: "CPF",
    phone: "Telefone",
    ip: "IP",
    tryAgain: "Verificar outro documento",
    backToHome: "Voltar ao início",
    privacy: "Nada é enviado: o seu navegador calcula o SHA-256 e lê o código do PDF localmente.",
    copy: "Copiar link",
    copied: "Copiado",
    captcha: "Confirme que você não é um robô e tente de novo.",
    failed: "Não foi possível verificar agora. Tente de novo em instantes.",
    chatOff: "",
  },
};

type T = typeof translations.en;
type Phase = "idle" | "hashing" | "checking";

function fmt(template: string, vars: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}

function MethodBadge({ method, t }: { method: string; t: T }) {
  const m = method.toLowerCase();
  const label = m.includes("gov") ? t.govbr : m.includes("typed") || m.includes("name") ? t.typed : t.draw;
  return <Badge variant="outline" className="text-xs">{label}</Badge>;
}

function SignerRow({ s, t, language }: { s: VerifiedSigner; t: T; language: string }) {
  const statusLabel = (t as any)[s.status] ?? s.status;
  const color = s.status === "signed" ? "text-[#4a7c3f]" : s.status === "declined" ? "text-red-600" : "text-amber-600";
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-t py-3 first:border-t-0" data-testid="verify-signer">
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#90BE6D]/20"><User className="h-4 w-4 text-[#4a7c3f]" /></div>
        <div className="min-w-0">
          <p className="font-medium">{s.name} <span className={`text-xs font-normal ${color}`}>· {statusLabel}</span></p>
          <p className="text-sm text-muted-foreground">{s.email}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {s.cpf && <span>{t.cpf} {s.cpf}</span>}
            {s.phone && <span>{t.phone} {s.phone}</span>}
            {s.signedAt && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(s.signedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</span>}
            {s.ipAddress && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t.ip} {s.ipAddress}</span>}
          </div>
        </div>
      </div>
      <MethodBadge method={s.signatureMethod} t={t} />
    </div>
  );
}

export default function PublicVerifyPage() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  // Both spellings of the route fill the code (the PDF footer and the QR use /verificar).
  const [, ptParams] = useRoute("/verificar/:code");
  const [, enParams] = useRoute("/verify/:code");
  const routeCode = (ptParams?.code || enParams?.code || "").toUpperCase();

  const [code, setCode] = useState(routeCode);
  const [hashInput, setHashInput] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [resultKey, setResultKey] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const captchaRef = useRef<string | undefined>(undefined);
  const autoRan = useRef<string | null>(null);

  const run = useCallback(async (input: { code?: string | null; hash?: string | null }) => {
    setPhase("checking");
    setNotice(null);
    try {
      let r: VerifyResult;
      try {
        r = await verifyCheck({ ...input, lang: language }, captchaRef.current);
      } catch (err) {
        if (err instanceof CaptchaRequiredError && err.siteKey) {
          captchaRef.current = await solveCaptcha(err.siteKey);
          r = await verifyCheck({ ...input, lang: language }, captchaRef.current);
        } else if (err instanceof CaptchaRequiredError) {
          setNotice(t.captcha);
          return;
        } else throw err;
      }
      setResult(r);
      setResultKey(`${Date.now()}`);
    } catch {
      setNotice(t.failed);
    } finally {
      setPhase("idle");
    }
  }, [language, t.captcha, t.failed]);

  // A code in the URL (QR) verifies by itself, once.
  useEffect(() => {
    if (routeCode && CODE_RX.test(routeCode) && autoRan.current !== routeCode) {
      autoRan.current = routeCode;
      setCode(routeCode);
      void run({ code: routeCode });
    }
  }, [routeCode, run]);

  const handleFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") return;
    setFileName(file.name);
    setResult(null);
    setPhase("hashing");
    setNotice(null);
    try {
      const bytes = await file.arrayBuffer();
      const [hash, found] = await Promise.all([
        sha256Hex(bytes),
        findVerificationCode(bytes).catch(() => null),
      ]);
      if (found) setCode(found);
      else if (!code) setNotice(t.codeNotFound);
      await run({ code: found || (CODE_RX.test(code) ? code : null), hash });
    } catch {
      // No Web Crypto / pdf.js here: the old upload path still works (the API hashes in memory, nothing is stored).
      try {
        setPhase("checking");
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(`${API_BASE_URL}/api/verify/upload`, { method: "POST", body: form });
        const json = await res.json();
        const data = json.data ?? json;
        setResult({
          status: data.isValid && data.hashMatch ? "authentic" : data.isValid ? "modified" : "unknown",
          completed: !!data.isValid, signedCount: data.signers?.length ?? 0, totalSigners: data.signers?.length ?? 0,
          documentName: data.documentName, verificationCode: data.verificationCode, signedAt: data.signedAt, certificateUrl: data.certificateUrl,
          signers: (data.signers ?? []).map((s: any) => ({ name: s.name, email: s.email, status: "signed", signedAt: s.signedAt, ipAddress: s.ipAddress, signatureMethod: s.signatureMethod })),
        });
        setResultKey(`${Date.now()}`);
      } catch {
        setNotice(t.failed);
      } finally {
        setPhase("idle");
      }
    }
  };

  const reset = () => { setResult(null); setFileName(null); setCode(""); setHashInput(""); setNotice(null); };
  const busy = phase !== "idle";
  const status = result?.status;
  const tone = status === "authentic" ? "ok" : status === "modified" ? "warn" : status === "registered" ? "info" : "bad";
  const toneClass = { ok: "border-[#90BE6D]", warn: "border-amber-400", info: "border-sky-400", bad: "border-red-400" }[tone];
  const headline = status === "authentic" ? t.authentic : status === "modified" ? t.modified : status === "registered" ? t.registered : t.unknown;
  const detail = !result ? "" :
    status === "authentic"
      ? (result.matchedVersion === "original" ? t.authenticUnsigned : result.completed ? t.authenticMsg : fmt(t.authenticPending, { n: result.totalSigners - result.signedCount }))
      : status === "modified"
        ? `${t.modifiedMsg}${result.matchedVersion === "original" ? ` ${t.modifiedOriginal}` : result.matchedVersion === "signed-partial" ? ` ${fmt(t.modifiedPartial, { n: result.signedCount, total: result.totalSigners })}` : ""}`
        : status === "registered" ? t.registeredMsg : t.unknownMsg;
  const mascotMood = status === "authentic" ? "happy" : status === "modified" ? "surprised" : status === "registered" ? "point-up" : status ? "sorry" : busy ? "thinking" : "wave";

  const copyLink = async () => {
    if (!result?.certificateUrl) return;
    try { await navigator.clipboard.writeText(result.certificateUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur-sm dark:bg-gray-900/80">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <Link href="/">
            <div className="flex cursor-pointer items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#90BE6D] to-[#4a7c3f]"><Shield className="h-5 w-5 text-white" /></div>
              <div><span className="text-xl font-bold text-[#2D3436] dark:text-white">Fill</span><span className="text-xl font-bold text-[#74C0FC]">Sign</span></div>
            </div>
          </Link>
          <div className="flex items-center gap-2"><LanguageToggle /><ThemeToggle /></div>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-6 sm:py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-center gap-4">
          <Mascot mood={mascotMood as any} size={72} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">{t.title}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">{t.subtitle}</p>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {result ? (
            <motion.div key="result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="space-y-4">
              <Card className={`border-2 shadow-xl ${toneClass}`} data-testid="verify-result" data-status={status}>
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0">
                      {status === "authentic" && <CheckCircle2 className="h-12 w-12 text-[#4a7c3f]" />}
                      {status === "modified" && <AlertTriangle className="h-12 w-12 text-amber-500" />}
                      {status === "registered" && <Info className="h-12 w-12 text-sky-500" />}
                      {status === "unknown" && <XCircle className="h-12 w-12 text-red-500" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-2xl font-bold" data-testid="verify-headline">{headline}</h2>
                      <p className="mt-1 text-gray-700 dark:text-gray-300" data-testid="verify-detail">{detail}</p>
                      {status === "unknown" && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {t.unknownIti} <a href={ITI_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[#4a7c3f] underline" data-testid="verify-iti-link">validar.iti.gov.br <ExternalLink className="h-3 w-3" /></a>
                        </p>
                      )}
                      {fileName && <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><FileCheck className="h-3 w-3" />{fileName}</p>}
                    </div>
                  </div>

                  {status === "registered" && (
                    <div
                      className="mt-5 cursor-pointer rounded-xl border-2 border-dashed border-sky-300 p-5 text-center hover:bg-sky-50 dark:hover:bg-sky-950/30"
                      onClick={() => fileInput.current?.click()}
                      onDrop={(e) => { e.preventDefault(); void handleFile(e.dataTransfer.files[0]); }}
                      onDragOver={(e) => e.preventDefault()}
                      data-testid="verify-drop-again"
                    >
                      <Upload className="mx-auto mb-1 h-6 w-6 text-sky-500" />
                      <p className="text-sm font-medium">{t.dropTitle}</p>
                      <p className="text-xs text-muted-foreground">{t.privacy}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {result.documentName && (
                <Card className="shadow-lg">
                  <CardContent className="space-y-4 p-6">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div><p className="text-sm text-muted-foreground">{t.documentName}</p><p className="flex items-center gap-2 font-medium"><FileText className="h-4 w-4 text-[#4a7c3f]" />{result.documentName}</p></div>
                      {result.signedAt && <div><p className="text-sm text-muted-foreground">{t.signedAt}</p><p className="font-medium">{new Date(result.signedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</p></div>}
                      {result.verificationCode && (
                        <div className="sm:col-span-2">
                          <p className="text-sm text-muted-foreground">{t.code}</p>
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="font-mono text-lg font-medium text-[#4a7c3f]" data-testid="verify-code">{result.verificationCode}</p>
                            {result.certificateUrl && (
                              <Button variant="outline" size="sm" onClick={copyLink} data-testid="verify-copy-link"><Copy className="mr-1 h-3 w-3" />{copied ? t.copied : t.copy}</Button>
                            )}
                            <img src={getApiUrl(`/api/verify/${result.verificationCode}/qr.svg`)} alt="QR" className="h-16 w-16 rounded border bg-white p-1" data-testid="verify-qr" />
                          </div>
                        </div>
                      )}
                    </div>
                    {result.signers.length > 0 && (
                      <div>
                        <p className="mb-1 flex items-center gap-2 text-sm font-medium"><User className="h-4 w-4 text-[#4a7c3f]" />{t.signers} ({result.signedCount}/{result.totalSigners})</p>
                        <div className="rounded-lg border px-3">
                          {result.signers.map((s, i) => <SignerRow key={i} s={s} t={t} language={language} />)}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {result.chatToken && <VerifyChat token={result.chatToken} language={language} resultKey={resultKey} />}

              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button onClick={reset} variant="outline" data-testid="verify-again"><Search className="mr-2 h-4 w-4" />{t.tryAgain}</Button>
                <Link href="/"><Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />{t.backToHome}</Button></Link>
              </div>
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="space-y-4">
              <Card className="shadow-xl">
                <CardContent className="p-5 sm:p-6">
                  <div
                    className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${busy ? "border-[#90BE6D] bg-[#90BE6D]/10" : "cursor-pointer border-gray-300 hover:border-[#90BE6D] hover:bg-[#90BE6D]/5"}`}
                    onClick={() => !busy && fileInput.current?.click()}
                    onDrop={(e) => { e.preventDefault(); if (!busy) void handleFile(e.dataTransfer.files[0]); }}
                    onDragOver={(e) => e.preventDefault()}
                    data-testid="verify-dropzone"
                  >
                    <input ref={fileInput} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { void handleFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="verify-file-input" />
                    {busy ? (
                      <div className="flex flex-col items-center gap-2"><Loader2 className="h-10 w-10 animate-spin text-[#4a7c3f]" /><p className="font-medium">{phase === "hashing" ? t.hashing : t.checking}</p></div>
                    ) : (
                      <>
                        <Upload className="mx-auto mb-2 h-12 w-12 text-[#4a7c3f]" />
                        <p className="text-lg font-semibold">{t.dropTitle}</p>
                        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t.dropHint}</p>
                      </>
                    )}
                  </div>
                  {notice && <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" data-testid="verify-notice">{notice}</p>}

                  <div className="mt-5">
                    <p className="mb-2 text-sm text-muted-foreground">{t.orCode}</p>
                    <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (CODE_RX.test(code)) void run({ code }); }}>
                      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t.codePlaceholder} className="text-center font-mono text-lg tracking-wider" maxLength={19} data-testid="verify-code-input" />
                      <Button type="submit" disabled={busy || !CODE_RX.test(code)} className="bg-gradient-to-r from-[#90BE6D] to-[#4a7c3f] text-white" data-testid="verify-code-button">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}<span className="ml-2 hidden sm:inline">{t.verifyButton}</span>
                      </Button>
                    </form>
                  </div>

                  <button type="button" onClick={() => setAdvanced((a) => !a)} className="mt-4 flex items-center gap-1 text-xs text-muted-foreground hover:underline" data-testid="verify-advanced">
                    <ChevronDown className={`h-3 w-3 transition-transform ${advanced ? "rotate-180" : ""}`} />{t.advanced}
                  </button>
                  {advanced && (
                    <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (/^[0-9a-f]{64}$/.test(hashInput)) void run({ hash: hashInput }); }}>
                      <Input value={hashInput} onChange={(e) => setHashInput(e.target.value.trim().toLowerCase())} placeholder={t.hashLabel} className="font-mono text-xs" maxLength={64} data-testid="verify-hash-input" />
                      <Button type="submit" variant="outline" disabled={busy || !/^[0-9a-f]{64}$/.test(hashInput)} data-testid="verify-hash-button"><Fingerprint className="h-4 w-4" /></Button>
                    </form>
                  )}
                </CardContent>
              </Card>
              <p className="flex items-center justify-center gap-2 text-center text-xs text-muted-foreground"><Lock className="h-3 w-3" />{t.privacy}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12 border-t pt-8 text-center text-sm text-muted-foreground">
          <p className="flex items-center justify-center gap-1"><ShieldCheck className="h-4 w-4" />FillSign</p>
        </div>
      </main>
    </div>
  );
}
